import React, { useEffect, useState } from "react";
import { BulkActionProvider } from "../../contexts/BulkActionContext";
import BulkActionPanel from "../../components/BulkActionPanel";
import { useNavigate, useLocation } from "react-router-dom";
import SidebarToggleButton from "../../components/SidebarToggleButton";
import { useAuth } from "../../useAuth";
import { useTranslation } from "react-i18next";
import {
  BarsOutlined,
  ArrowLeftOutlined,
  ArrowRightOutlined,
  AppstoreOutlined,
  LoginOutlined,
  ShopOutlined,
  UserOutlined,
  DesktopOutlined,
  BarChartOutlined,
  DollarOutlined,
  HistoryOutlined,
  SettingOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import { Button, Typography, Divider, Dropdown, Tag } from "antd";
import MenuItem from "../../components/MenuItem";
import LanguageToggle from "../../components/languageToggle";
import i18n from "../../i18n";
import { mspApprovalApi } from "../../api";

const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isSidebarMobileOpen, setIsSidebarMobileOpen] = useState(false);

  const { role, logout, userName, token } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const isRtl = i18n.language !== "en";

  // Poll pending-approvals count so the nav badge stays fresh.
  const [pendingCount, setPendingCount] = useState<number>(0);
  const [conflictCount, setConflictCount] = useState<number>(0);
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    const load = () => {
      mspApprovalApi.count(token as string)
        .then((r) => { if (!cancelled) setPendingCount(r.pending || 0); }).catch(() => {});
      mspApprovalApi.countConflicts(token as string)
        .then((r) => { if (!cancelled) setConflictCount(r.conflicts || 0); }).catch(() => {});
    };
    load();
    const id = setInterval(load, 60_000);
    return () => { cancelled = true; clearInterval(id); };
  }, [token]);

  // Auto-collapse sidebar below lg on initial render (handled via CSS, but keep state in sync)
  const toggleSidebar = () => {
    setIsSidebarOpen((prev) => !prev);
  };

  useEffect(() => {
    // Nothing to do on mount; kept for symmetry with ECR layout
  }, [role, navigate]);

  /** Breadcrumb: build segments from the current pathname */
  const buildBreadcrumb = () => {
    const segments = location.pathname
      .split("/")
      .filter(Boolean)
      .map((seg) => seg.charAt(0).toUpperCase() + seg.slice(1).replace(/-/g, " "));
    return segments;
  };

  const MenuContent: React.FC = () => (
    <div className="bg-neo-primary flex flex-col justify-between h-full no-scrollbar overflow-y-scroll rounded-3xl py-8 shadow-xl">
      {/* Logo / Wordmark */}
      <div className="px-6">
        <div className="rounded-xl p-4">
          <Typography className="text-white font-poppins-700 text-center text-2xl">
            NeoPaaS
          </Typography>
          {import.meta.env.VITE_ENV_LABEL && (
            <Typography className="text-white/80 font-poppins-300 text-center text-xs mt-1">
              {import.meta.env.VITE_ENV_LABEL}
            </Typography>
          )}
        </div>
      </div>

      {/* Nav Items */}
      <div className="mt-12 px-6 flex flex-col gap-y-2 flex-1">
        <Typography className="font-poppins-600 text-xs text-white/40 uppercase tracking-wider mb-2 px-3">
          {t("menu")}
        </Typography>

        <div className="menu-item-stagger">
          <MenuItem
            display={true}
            current={location.pathname}
            location={"/msp-portal"}
            icon={<AppstoreOutlined />}
            title={t("dashboard")}
            to={() => {
              navigate("/msp-portal");
              setIsSidebarMobileOpen(false);
            }}
          />
        </div>

        <div className="menu-item-stagger">
          <MenuItem
            display={true}
            current={location.pathname}
            location={"/msp-portal/merchants"}
            icon={<ShopOutlined />}
            title={t("msp.merchants.title", { defaultValue: "Merchants" })}
            to={() => {
              navigate("/msp-portal/merchants");
              setIsSidebarMobileOpen(false);
            }}
          />
        </div>

        <div className="menu-item-stagger">
          <MenuItem
            display={true}
            current={location.pathname}
            location={"/msp-portal/pending-approvals"}
            icon={<ClockCircleOutlined />}
            title={t("msp.pendingApprovals.title", { defaultValue: "Pending Approvals" })}
            badge={pendingCount > 0 ? pendingCount : undefined}
            to={() => {
              navigate("/msp-portal/pending-approvals");
              setIsSidebarMobileOpen(false);
            }}
          />
        </div>

        {/* <div className="menu-item-stagger">
          <MenuItem
            display={true}
            current={location.pathname}
            location={"/msp-portal/conflicts"}
            icon={<ExclamationCircleOutlined />}
            title={t("msp.conflicts.title", { defaultValue: "Ownership Conflicts" })}
            badge={conflictCount > 0 ? conflictCount : undefined}
            to={() => {
              navigate("/msp-portal/conflicts");
              setIsSidebarMobileOpen(false);
            }}
          />
        </div> */}

        <div className="menu-item-stagger">
          <MenuItem
            display={true}
            current={location.pathname}
            location={"/msp-portal/terminals"}
            icon={<DesktopOutlined />}
            title={t("msp.terminals.title", { defaultValue: "Terminals" })}
            to={() => {
              navigate("/msp-portal/terminals");
              setIsSidebarMobileOpen(false);
            }}
          />
        </div>

        <div className="menu-item-stagger">
          <MenuItem
            display={true}
            current={location.pathname}
            location={"/msp-portal/transaction-report"}
            icon={<BarChartOutlined />}
            title={t("msp.transactionReport.title", { defaultValue: "Transaction Report" })}
            to={() => {
              navigate("/msp-portal/transaction-report");
              setIsSidebarMobileOpen(false);
            }}
          />
        </div>

        <div className="menu-item-stagger">
          <MenuItem
            display={true}
            current={location.pathname}
            location={"/msp-portal/billing"}
            icon={<DollarOutlined />}
            title={t("msp.billing.title", { defaultValue: "Billing" })}
            to={() => {
              navigate("/msp-portal/billing");
              setIsSidebarMobileOpen(false);
            }}
          />
        </div>

        <div className="menu-item-stagger">
          <MenuItem
            display={true}
            current={location.pathname}
            location={"/msp-portal/audit-trail"}
            icon={<HistoryOutlined />}
            title={t("msp.audit.title", { defaultValue: "Activity Log" })}
            to={() => {
              navigate("/msp-portal/audit-trail");
              setIsSidebarMobileOpen(false);
            }}
          />
        </div>

        <div className="menu-item-stagger">
          <MenuItem
            display={true}
            current={location.pathname}
            location={"/msp-portal/settings"}
            icon={<SettingOutlined />}
            title={t("msp.settings.title", { defaultValue: "Settings" })}
            to={() => {
              navigate("/msp-portal/settings");
              setIsSidebarMobileOpen(false);
            }}
          />
        </div>
      </div>

      {/* Bottom: logout only */}
      <div className="p-6">
        <Divider className="border-white/20" />

        <MenuItem
          display={true}
          icon={<LoginOutlined />}
          title={t("logout")}
          to={logout}
        />
      </div>
    </div>
  );

  const breadcrumb = buildBreadcrumb();

  return (
    <BulkActionProvider>
      <BulkActionPanel />
      <div className={`flex min-h-screen dark-mode overflow-x-hidden`}>
        {/* ── Desktop Sidebar ── */}
        <div
          className={`fixed inset-y-0 ${isRtl ? "right-0" : "left-0"} w-64 p-3 px-1.5 transform transition-transform
          duration-300 z-10 hidden lg:block
          ${isSidebarOpen
            ? "translate-x-0"
            : isRtl
              ? "translate-x-full"
              : "-translate-x-full"
          }`}
        >
          <MenuContent />
        </div>

        {/* ── Mobile Sidebar Overlay ── */}
        {isSidebarMobileOpen && (
          <div
            className="fixed inset-0 bg-black/50 z-40 lg:hidden"
            onClick={() => setIsSidebarMobileOpen(false)}
          />
        )}

        {/* ── Mobile Sidebar Drawer ── */}
        <div
          className={`fixed inset-0 ${isRtl ? "right-0" : "left-0"} w-full h-full p-3 px-1.5 transform transition-transform
          duration-300 z-50 lg:hidden
          ${isSidebarMobileOpen
            ? "translate-x-0"
            : isRtl
              ? "translate-x-full"
              : "-translate-x-full"
          }`}
        >
          <div className="flex flex-col h-full">
            <div className="flex p-3 justify-end w-full">
              <Button
                type="primary"
                icon={isRtl ? <ArrowRightOutlined /> : <ArrowLeftOutlined />}
                onClick={() => setIsSidebarMobileOpen(false)}
                className="bg-indigo-600 border-indigo-600 hover:bg-indigo-700"
              />
            </div>
            <MenuContent />
          </div>
        </div>

        {/* ── Main Content ── */}
        <div
          className={`flex flex-col flex-1 min-w-0 transition-all p-2 sm:p-3 gap-y-3 sm:gap-y-4 duration-300
          ${isSidebarOpen ? (isRtl ? "lg:mr-64" : "lg:ml-64") : ""}`}
        >
          {/* ── Top Bar ── */}
          <div
            className={`flex ${isRtl ? "flex-row-reverse" : "flex-row"} dark-mode-header justify-between p-3 sm:p-4 px-3 sm:px-6 gap-x-2 sm:gap-x-3 min-h-16 sm:min-h-24 items-center rounded-xl sm:rounded-2xl shadow-md`}
          >
            {/* Left: hamburger (mobile) + breadcrumb */}
            <div className={`flex items-center gap-x-2 sm:gap-x-4 ${isRtl ? "flex-row-reverse" : ""}`}>
              <Button
                className="text-white bg-indigo-600 hover:bg-indigo-700 transition-colors lg:hidden border-0"
                type="text"
                size="large"
                icon={<BarsOutlined />}
                onClick={() => setIsSidebarMobileOpen(true)}
                aria-label="Open navigation menu"
              />

              {/* Logo when sidebar is closed on desktop */}
              {!isSidebarOpen && (
                <div className="hidden lg:flex flex-col items-start animate-fadeIn">
                  <Typography className="text-indigo-600 font-poppins-700 text-xl leading-tight">
                    NeoPaaS MSP
                  </Typography>
                  {import.meta.env.VITE_ENV_LABEL && (
                    <Typography className="text-indigo-600/60 font-poppins-400 text-xs mt-0.5">
                      {import.meta.env.VITE_ENV_LABEL}
                    </Typography>
                  )}
                </div>
              )}

              {/* Breadcrumb */}
              <nav aria-label="Breadcrumb" className="hidden sm:flex items-center gap-x-1">
                {breadcrumb.map((seg, idx) => (
                  <React.Fragment key={idx}>
                    {idx > 0 && (
                      <span className="text-slate-400 text-sm mx-1">/</span>
                    )}
                    <Typography
                      className={`text-sm ${
                        idx === breadcrumb.length - 1
                          ? "font-poppins-600 text-slate-700"
                          : "font-poppins-400 text-slate-400"
                      }`}
                    >
                      {seg}
                    </Typography>
                  </React.Fragment>
                ))}
              </nav>
            </div>

            {/* Right: language toggle + user dropdown */}
            <div className={`flex items-center justify-end gap-x-2 sm:gap-x-3 ${isRtl ? "flex-row-reverse" : ""}`}>
              {/* Language toggle */}
              <LanguageToggle icon={true} />

              {/* User dropdown */}
              <Dropdown
                menu={{
                  items: [
                    {
                      key: "user-info",
                      label: (
                        <div className="px-2 py-2 min-w-[200px]">
                          <div className="flex items-center gap-x-3 mb-3 pb-3 border-b border-slate-200">
                            <div className="w-12 h-12 rounded-full bg-indigo-600 flex justify-center items-center shadow-md">
                              <UserOutlined className="text-white text-xl" />
                            </div>
                            <div className="flex flex-col">
                              <Typography className="font-poppins-600 text-sm text-gray-800">
                                {userName}
                              </Typography>
                              <Tag
                                color="blue"
                                className="mt-0.5 w-fit text-xs border-0 bg-indigo-100 text-indigo-700"
                              >
                                MSP
                              </Tag>
                            </div>
                          </div>
                          <div className="space-y-2">
                            <div className="flex items-center gap-x-2 text-xs text-gray-600">
                              <ShopOutlined className="text-indigo-600" />
                              <span className="font-poppins-400">MSP Portal Account</span>
                            </div>
                          </div>
                        </div>
                      ),
                      disabled: true,
                    },
                    { type: "divider" },
                    {
                      key: "logout",
                      label: (
                        <div className="flex items-center gap-x-2 text-red-600 hover:text-red-700">
                          <LoginOutlined />
                          <span className="font-poppins-500">{t("logout")}</span>
                        </div>
                      ),
                      onClick: () => logout(),
                    },
                  ],
                }}
                trigger={["click"]}
                placement="bottomRight"
              >
                <div className="items-center flex gap-x-2 sm:gap-x-3 bg-slate-50 rounded-lg sm:rounded-xl p-1.5 sm:p-2 pr-2 sm:pr-4 shadow-sm border border-slate-200 hover:border-indigo-300 transition-all duration-200 cursor-pointer">
                  <div className="w-8 h-8 sm:w-12 sm:h-12 rounded-full bg-indigo-600 flex justify-center items-center shadow-sm">
                    <UserOutlined className="text-white text-base sm:text-xl" />
                  </div>
                  <div className="hidden sm:flex flex-col justify-center">
                    <Typography className="font-poppins-600 text-sm text-gray-800">
                      {userName}
                    </Typography>
                    <Typography className="font-poppins-400 text-xs text-indigo-600/70 uppercase tracking-wide">
                      MSP
                    </Typography>
                  </div>
                </div>
              </Dropdown>
            </div>
          </div>

          {/* Sidebar toggle button (desktop) */}
          <div className="hidden lg:block">
            <SidebarToggleButton isOpen={isSidebarOpen} onClick={toggleSidebar} />
          </div>

          {/* Page content */}
          <div className="py-3 px-2 sm:py-4 sm:px-4 lg:py-8 lg:px-8 flex flex-1 bg-white rounded-xl sm:rounded-2xl shadow-sm border border-slate-100">
            {children}
          </div>
        </div>
      </div>
    </BulkActionProvider>
  );
};

export { Layout };
